import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Bot, Send, Trash2, User, Loader2, Lightbulb, Package, Calculator, Building2, ChevronDown } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  { text: "Estime les matériaux pour une dalle béton de 50m²", icon: Calculator, category: "Béton" },
  { text: "Combien de briques pour un mur de 20m² ?", icon: Package, category: "Maçonnerie" },
  { text: "Quelles isolations recommandes-tu pour une façade belge ?", icon: Lightbulb, category: "Isolation" },
  { text: "Calcule la peinture nécessaire pour 3 pièces de 15m²", icon: Package, category: "Finitions" },
  { text: "Optimise le budget pour une rénovation complète", icon: Lightbulb, category: "Budget" },
  { text: "Quels matériaux pour une charpente bois de 80m² ?", icon: Calculator, category: "Bois" }
];

function MessageBubble({ message }) {
  const isUser = message.role === 'user';
  const formatContent = (content) => {
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/^### (.*?)$/gm, '<h3 class="font-bold text-base mt-3 mb-1 text-[#1E293B]">$1</h3>')
      .replace(/^## (.*?)$/gm, '<h2 class="font-bold text-lg mt-3 mb-1 text-[#1E293B]">$1</h2>')
      .replace(/^- (.*?)$/gm, '<li class="ml-4 list-disc text-sm">$1</li>')
      .replace(/^(\d+)\. (.*?)$/gm, '<li class="ml-4 list-decimal text-sm">$2</li>')
      .replace(/\n/g, '<br/>');
  };

  return (
    <div className={`flex gap-2.5 ${isUser ? 'justify-end' : 'justify-start'} animate-fadeIn`}>
      {!isUser && (
        <div className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0 self-end">
          <Bot className="w-4 h-4 text-[#3B82F6]" />
        </div>
      )}
      <div className={`max-w-[85%] rounded-xl p-3.5 ${isUser ? 'bg-gradient-to-br from-[#3B82F6] to-[#2563EB] text-white shadow-md shadow-blue-200 rounded-br-sm' : 'bg-[#F8FAFC] border border-[#E2E8F0] text-[#1E293B] rounded-bl-sm'}`}>
        <div
          className="text-sm leading-relaxed"
          dangerouslySetInnerHTML={{ __html: formatContent(message.content) }}
        />
      </div>
      {isUser && (
        <div className="w-8 h-8 rounded-lg bg-orange-50 border border-orange-100 flex items-center justify-center flex-shrink-0 self-end">
          <User className="w-4 h-4 text-[#F97316]" />
        </div>
      )}
    </div>
  );
}

export default function Assistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [histLoading, setHistLoading] = useState(true);
  const [chantiers, setChantiers] = useState([]);
  const [selectedChantier, setSelectedChantier] = useState("none");
  const [sessionId] = useState(() => `vgi-${Date.now()}`);
  const messagesEndRef = useRef(null);
  const { t } = useLanguage();

  useEffect(() => {
    fetchInit();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchInit = async () => {
    try {
      const [histRes, chantiersRes] = await Promise.all([
        axios.get(`${API}/assistant/history?session_id=${sessionId}`),
        axios.get(`${API}/chantiers`)
      ]);
      setMessages(histRes.data);
      setChantiers(chantiersRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setHistLoading(false);
    }
  };

  const sendMessage = async (text = input) => {
    if (!text.trim() || loading) return;
    const userMsg = { role: "user", content: text, id: Date.now().toString() };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const payload = {
        message: text,
        session_id: sessionId,
        chantier_id: selectedChantier !== "none" ? selectedChantier : null
      };
      const res = await axios.post(`${API}/assistant/chat`, payload);
      setMessages(prev => [...prev, { role: "assistant", content: res.data.response, id: Date.now().toString() }]);
    } catch (e) {
      toast.error(t('errorGeneric'));
      setMessages(prev => prev.filter(m => m.id !== userMsg.id));
    } finally {
      setLoading(false);
    }
  };

  const clearHistory = async () => {
    if (!window.confirm(t('confirmClearHistory'))) return;
    try {
      await axios.delete(`${API}/assistant/history`);
      setMessages([]);
      toast.success(t('historyCleared'));
    } catch (e) { toast.error(t('errorGeneric')); }
  };

  const selectedChantierName = chantiers.find(c => c.id === selectedChantier)?.nom;

  if (histLoading) return (
    <div className="flex items-center justify-center h-[80vh]">
      <div className="w-8 h-8 border-2 border-[#3B82F6] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div data-testid="assistant-page" className="h-[calc(100vh-120px)] flex flex-col animate-fadeIn">
      {/* Header */}
      <div className="flex items-start justify-between mb-4 gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[#1E293B] uppercase tracking-tight font-['Barlow_Condensed']">{t('assistantTitle')}</h1>
          <p className="text-[#94A3B8] text-sm mt-0.5">{t('assistantSubtitle')}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {messages.length > 0 && (
            <Button variant="outline" size="sm" onClick={clearHistory} className="border-[#E2E8F0] text-[#94A3B8] hover:text-red-500 hover:border-red-200 hover:bg-red-50 rounded-lg">
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Chantier context selector */}
      <div className="mb-3">
        <Select value={selectedChantier} onValueChange={setSelectedChantier}>
          <SelectTrigger data-testid="select-chantier-ia" className="bg-white border-[#E2E8F0] text-[#1E293B] rounded-xl shadow-sm h-9 text-sm">
            <Building2 className="w-4 h-4 text-[#94A3B8] mr-2 flex-shrink-0" />
            <SelectValue placeholder={t('selectChantier')} />
          </SelectTrigger>
          <SelectContent className="bg-white border-[#E2E8F0] text-[#1E293B] shadow-lg">
            <SelectItem value="none">Aucun chantier spécifique</SelectItem>
            {chantiers.map(c => (
              <SelectItem key={c.id} value={c.id}>
                {c.nom} — {c.progression}%
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selectedChantier !== "none" && selectedChantierName && (
          <p className="text-xs text-[#3B82F6] mt-1 ml-2">
            Context actif : {selectedChantierName}
          </p>
        )}
      </div>

      {/* Chat */}
      <Card className="flex-1 bg-white border-[#E2E8F0] flex flex-col overflow-hidden shadow-sm">
        <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center min-h-[300px]">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#3B82F6] to-[#2563EB] flex items-center justify-center mb-4 shadow-lg shadow-blue-200">
                <Bot className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-[#1E293B] mb-1">{t('assistantWelcome')}</h3>
              <p className="text-[#64748B] text-sm max-w-sm mb-5">
                {t('assistantDescription')}

              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-xl">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    data-testid={`suggestion-${i}`}
                    onClick={() => sendMessage(s.text)}
                    className="flex items-start gap-3 p-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-left hover:border-[#3B82F6]/30 hover:bg-blue-50/40 transition-all group"
                  >
                    <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-100 transition-colors">
                      <s.icon className="w-3.5 h-3.5 text-[#3B82F6]" />
                    </div>
                    <div>
                      <span className="text-[10px] font-medium text-[#3B82F6] uppercase tracking-wider block mb-0.5">{s.category}</span>
                      <p className="text-xs text-[#64748B] group-hover:text-[#1E293B] transition-colors leading-snug">{s.text}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map((msg, idx) => <MessageBubble key={msg.id || idx} message={msg} />)}
              {loading && (
                <div className="flex gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-[#3B82F6]" />
                  </div>
                  <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl rounded-bl-sm p-3.5">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 text-[#3B82F6] animate-spin" />
                      <span className="text-sm text-[#94A3B8]">{t('loading')}</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </CardContent>

        {/* Input */}
        <div className="p-3 border-t border-[#F1F5F9] bg-[#F8FAFC]">
          {selectedChantier !== "none" && (
            <div className="flex items-center gap-1.5 mb-2 px-1">
              <div className="w-1.5 h-1.5 rounded-full bg-[#3B82F6] animate-pulse" />
              <span className="text-xs text-[#3B82F6]">Contexte : {selectedChantierName}</span>
            </div>
          )}
          <div className="flex gap-2">
            <Textarea
              data-testid="input-message"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              placeholder={t('askQuestion')}
              className="flex-1 bg-white border-[#E2E8F0] text-[#1E293B] placeholder:text-[#CBD5E1] rounded-xl min-h-[44px] max-h-[120px] text-sm resize-none shadow-sm"
              disabled={loading}
            />
            <Button
              data-testid="btn-send-message"
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading}
              className="bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-xl px-4 btn-press disabled:opacity-40 shadow-md shadow-blue-200 self-end h-11"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
          <p className="text-[10px] text-[#CBD5E1] mt-1.5 ml-1">{t('pressEnterToSend')}</p>
        </div>
      </Card>
    </div>
  );
}
